<script setup lang="ts">
import type { SiteSectionCategoryId, SiteSectionId, V4HSiteId } from "@/data/common/ids";
import type { DateRange } from "@/domains/v4b/site-consumption/site-consumption.types";

import { computed } from "vue";

import { useDashboardMonoController } from "@/app/data/dashboard/dashboard-mono.controller";
import { EDashboardType } from "@/app/data/dashboard/dashboard.types";
import { useDashboardMonositeQueries } from "@/composables/dashboard/use-dashboard-monosite-queries";
import { DATA_TEST_IDS } from "@/constants/data-test-ids/data-test-ids.constant";
import { keyOf, useTypedIntl } from "@/locales/typed-intl";
import $date from "@/utils/date";

import TemporalConsumptionChart from "@/app/data/dashboard/cards/common/temporal-consumption-chart.vue";
import ContactVoltalisButton from "@/components/contact/contact-voltalis-button.vue";
import HospitalityButton from "@/components/ui/buttons/hospitality-button.vue";

interface IProps {
  fromDay: Date;
  toDay: Date;
  siteId: V4HSiteId;
  selectedSectionIds?: Array<SiteSectionId>;
  selectedSectionCategoryIds?: Array<SiteSectionCategoryId>;
}

const props = defineProps<IProps>();

const { t, k } = useTypedIntl();

const computedSiteId = computed(() => props.siteId);
const dateRange = computed<DateRange>(() => {
  return {
    from: $date.formatLocalDate(props.fromDay),
    to: $date.formatLocalDate(props.toDay),
  };
});
const computedSelectedSectionIds = computed<Array<SiteSectionId>>(() => {
  if (!props.selectedSectionIds) return [];
  return props.selectedSectionIds;
});
const computedSelectedSectionCategoryIds = computed<Array<SiteSectionCategoryId>>(() => {
  if (!props.selectedSectionCategoryIds) return [];
  return props.selectedSectionCategoryIds;
});

const { siteConsumption, missingInfos, isSiteConsumptionPending } = useDashboardMonositeQueries(
  computedSiteId,
  dateRange,
  computedSelectedSectionIds,
  computedSelectedSectionCategoryIds,
);

const { hasNoDsoDataAndNoInstalledEquipments, formattedConsumptionCurvesSeries } = useDashboardMonoController(
  siteConsumption,
  missingInfos,
  undefined,
);
</script>

<template>
  <TemporalConsumptionChart
    :data-test-id="DATA_TEST_IDS.views.dashboard.charts.consumptionEvolutionChart"
    :from-day="props.fromDay"
    :has-no-data="hasNoDsoDataAndNoInstalledEquipments"
    :is-loading="isSiteConsumptionPending"
    :series="formattedConsumptionCurvesSeries"
    :to-day="props.toDay"
    :type="EDashboardType.MONOSITE"
  >
    <template #noDataMessage>
      <div class="flex flex-col gap-4">
        <h5>{{ t(k.data.dashboard.noDataChartMessage.title) }}</h5>
        <i18n-t
          :keypath="keyOf(k.data.dashboard.noDataChartMessage.noInstalledDevices)"
          tag="p"
        >
          <template #installedDevices>
            <b>
              {{ t(k.data.dashboard.noDataChartMessage.installedDevices) }}
            </b>
          </template>
        </i18n-t>
        <ContactVoltalisButton>
          <template #monosite>
            <HospitalityButton
              class="flex w-full"
              aria-label="contact-voltalis-link"
              :data-test-id="DATA_TEST_IDS.views.dashboard.buttons.contactVoltalisBtn"
              :label="t(k.components['contact-voltalis-dialog'].title)"
              severity="secondary"
            />
          </template>
        </ContactVoltalisButton>
      </div>
    </template>
  </TemporalConsumptionChart>
</template>
